// Kişiye özel sol menü: sürükle-bırak sıralama + favoriler (sunucuda saklanır)
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { DndContext, closestCenter, MouseSensor, TouchSensor, KeyboardSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import { GripVertical, Star, Pencil, Check, RotateCcw, HelpCircle, Search, X } from 'lucide-react';
import { MenuDemoDialog } from './MenuDemoDialog';
import { splitGroups, MENU_GROUPS } from './menuGroups';
import { NavGroup } from './NavGroup';

const DEFAULT_ORDER = MENU_GROUPS.flatMap(g => g.paths);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const arrange = (items, prefs) => {
  const idx = (p) => { const i = prefs.order.indexOf(p); if (i !== -1) return i; const d = DEFAULT_ORDER.indexOf(p); return d === -1 ? 2000 + items.findIndex(x => x.path === p) : 1000 + d; };
  const sorted = [...items].sort((a, b) => idx(a.path) - idx(b.path));
  return [sorted.filter(i => prefs.favorites.includes(i.path)), sorted.filter(i => !prefs.favorites.includes(i.path))];
};

const SortRow = ({ item, fav, onStar }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.path });
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }} {...attributes} {...listeners}
      className={`relative flex cursor-grab items-center gap-2 rounded-lg border px-2 py-2 text-sm active:cursor-grabbing ${isDragging ? 'z-10 scale-[1.03] border-orange-300 bg-orange-50 shadow-lg ring-1 ring-orange-200' : 'border-transparent bg-white hover:bg-slate-50'}`}
      data-testid={`menu-edit-row-${item.path.replace('/', '') || 'dashboard'}`}>
      <span className="rounded p-1 text-slate-400" aria-hidden="true" data-testid="menu-drag-handle">
        <GripVertical className="h-4 w-4" />
      </span>
      <item.icon className="h-4 w-4 shrink-0 text-slate-500" />
      <span className="flex-1 truncate text-slate-700">{item.label}</span>
      <button type="button" onPointerDown={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()} onClick={() => onStar(item.path)} className="cursor-pointer rounded p-1 transition-transform hover:scale-110" aria-label={fav ? 'Favoriden çıkar' : 'Favorilere ekle'} data-testid="menu-star-toggle">
        <Star className={`h-4 w-4 ${fav ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`} />
      </button>
    </div>
  );
};

const Group = ({ title, testid, children, divider }) => (
  <div className={divider ? 'mt-2 border-t border-slate-200 pt-2' : ''} data-testid={testid}>
    <p className="flex items-center gap-2 px-3 pb-1.5 pt-2 text-xs font-bold uppercase tracking-wide text-slate-800"><span className="h-3.5 w-1 rounded-full bg-orange-500" />{title}</p>
    <div className="space-y-1">{children}</div>
  </div>
);

const SortGroup = ({ list, fav, onMove, onStar, sensors }) => (
  <DndContext sensors={sensors} collisionDetection={closestCenter} modifiers={[restrictToVerticalAxis, restrictToParentElement]}
    onDragStart={({ activatorEvent }) => { if (activatorEvent?.type?.startsWith('touch')) navigator.vibrate?.(15); }} onDragEnd={({ active, over }) => {
    if (over && active.id !== over.id) onMove(list.map(i => i.path), active.id, over.id);
  }}>
    <SortableContext items={list.map(i => i.path)} strategy={verticalListSortingStrategy}>
      <div className="relative space-y-1">{list.map(item => <SortRow key={item.path} item={item} fav={fav} onStar={onStar} />)}</div>
    </SortableContext>
  </DndContext>
);

export const useMenuPrefs = () => {
  const [prefs, setPrefs] = useState({ order: [], favorites: [] });
  useEffect(() => { axios.get(`${API}/auth/menu-prefs`).then(r => setPrefs(r.data)).catch(() => {}); }, []);
  const save = (next) => {
    setPrefs(next);
    axios.put(`${API}/auth/menu-prefs`, next).catch(() => toast.error('Menü düzeni kaydedilemedi'));
  };
  return [prefs, save];
};

const useGroupOpen = (userId) => {
  const key = `menu-groups-open-${userId}`;
  const [open, setOpen] = useState(() => { try { return JSON.parse(localStorage.getItem(key)) || {}; } catch { return {}; } });
  const toggle = (g, cur) => setOpen(o => { const n = { ...o, [g]: !(o[g] ?? cur) }; localStorage.setItem(key, JSON.stringify(n)); return n; });
  return [open, toggle];
};

const MenuSearch = ({ value, onChange }) => (
  <div className="relative mb-2" data-testid="menu-search">
    <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
    <input value={value} onChange={e => onChange(e.target.value)} placeholder="Menüde ara..."
      className="h-8 w-full rounded-md border border-slate-200 bg-slate-50 pl-8 pr-7 text-xs text-slate-700 outline-none transition-colors placeholder:text-slate-400 focus:border-orange-400 focus:bg-white"
      data-testid="menu-search-input" />
    {value && <button type="button" onClick={() => onChange('')} className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-400 hover:text-slate-700" aria-label="Aramayı temizle" data-testid="menu-search-clear"><X className="h-3.5 w-3.5" /></button>}
  </div>
);

export const SidebarNav = ({ items, renderItem, prefs, save, userId, onEditChange, searchable }) => {
  const [editing, setEditing] = useState(false);
  const [query, setQuery] = useState('');
  const [groupOpen, toggleGroup] = useGroupOpen(userId);
  const { pathname } = useLocation();
  const [demo, setDemo] = useState(false);
  const demoKey = `menu-demo-seen-${userId}`;
  const toggleEdit = () => {
    if (!editing && !localStorage.getItem(demoKey)) { localStorage.setItem(demoKey, '1'); setDemo(true); }
    onEditChange?.(!editing);
    setEditing(e => !e);
  };
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const [favs, rest] = arrange(items, prefs);
  const groups = splitGroups(rest);
  const paths = (l) => l.map(i => i.path);
  const move = (groupKey) => (list, from, to) => {
    const moved = arrayMove(list, list.indexOf(from), list.indexOf(to));
    save({ ...prefs, order: groupKey === 'fav' ? [...moved, ...paths(rest)] : [...paths(favs), ...groups.flatMap(g => (g.key === groupKey ? moved : paths(g.items)))] });
  };
  const isActive = (p) => (p === '/' ? pathname === '/' : pathname.startsWith(p));
  const q = query.trim().toLocaleLowerCase('tr');
  const found = q ? [...favs, ...rest].filter(i => i.label.toLocaleLowerCase('tr').includes(q)) : null;
  const star = (path) => {
    const on = prefs.favorites.includes(path);
    const others = (l) => paths(l).filter(p => p !== path);
    save(on
      ? { order: [...others(favs), path, ...others(rest)], favorites: prefs.favorites.filter(p => p !== path) }
      : { order: [...paths(favs), path, ...others(rest)], favorites: [...prefs.favorites, path] });
  };

  return (
    <nav className="flex-1 overflow-y-auto p-4" data-testid="sidebar-nav">
      <div className="mb-2 flex flex-col items-center gap-1.5">
        <div className="flex items-center justify-center gap-1">
          <button type="button" onClick={toggleEdit} className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs transition-[background-color,color,transform] active:scale-95 ${editing ? 'border-orange-500 bg-orange-500 font-semibold text-white hover:bg-orange-600' : 'border-slate-200 text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`} data-testid="menu-edit-toggle">
            {editing ? <><Check className="h-3.5 w-3.5" />Bitti</> : <><Pencil className="h-3.5 w-3.5" />Menüyü Düzenle</>}
          </button>
          <button type="button" onClick={() => setDemo(true)} className="flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900" aria-label="Nasıl yapılır?" title="Nasıl yapılır?" data-testid="menu-demo-btn">
            <HelpCircle className="h-3.5 w-3.5" />
          </button>
        </div>
        {editing && (
          <button type="button" onClick={() => { save({ order: [], favorites: [] }); toast.success('Menü varsayılan düzene döndü'); }} className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] text-slate-500 hover:bg-slate-100 hover:text-slate-800" data-testid="menu-reset-btn">
            <RotateCcw className="h-3 w-3" />Varsayılana Dön
          </button>
        )}
      </div>
      <MenuDemoDialog open={demo} onClose={() => setDemo(false)} />
      {editing ? (
        <div className="select-none space-y-2">
          <p className="px-1 text-[11px] leading-4 text-slate-400">Menüye basılı tutup kendi başlığı içinde yukarı/aşağı sürükleyin, yıldızla favorilere ekleyin.</p>
          {favs.length > 0 && <Group title="Favoriler" testid="menu-edit-favorites"><SortGroup list={favs} fav onMove={move('fav')} onStar={star} sensors={sensors} /></Group>}
          {groups.map((g, i) => <Group key={g.key} title={g.title} divider={i > 0 || favs.length > 0} testid={`menu-edit-group-${g.key}`}><SortGroup list={g.items} fav={false} onMove={move(g.key)} onStar={star} sensors={sensors} /></Group>)}
        </div>
      ) : (
        <div className="space-y-1">
          {searchable && <MenuSearch value={query} onChange={setQuery} />}
          {found ? (
            found.length ? <div className="space-y-1" data-testid="menu-search-results">{found.map(renderItem)}</div>
              : <p className="px-3 py-4 text-center text-xs text-slate-400" data-testid="menu-search-empty">Eşleşen menü bulunamadı</p>
          ) : (<>
            {favs.length > 0 && <Group title="Favoriler" testid="nav-favorites">{favs.map(renderItem)}</Group>}
            {groups.map(g => (
              <NavGroup key={g.key} group={g} divider={favs.length > 0} open={groupOpen[g.key] ?? g.items.some(i => isActive(i.path))} onToggle={() => toggleGroup(g.key, g.items.some(i => isActive(i.path)))}>
                {g.items.map(renderItem)}
              </NavGroup>
            ))}
          </>)}
        </div>
      )}
    </nav>
  );
};
